// apps/admin/src/pages/users/RevokeCreditDialog.tsx
//
// Phase 3.4b Wave 7 Task 16 — Admin "Revoke credit" confirmation dialog.
//
// Per CONTEXT.md
//   • Credit lifecycle. Revoke is the terminal admin-driven
//                transition; reason is mandatory (10-500 chars) for the
//                audit trail, and the merchant remains liable for the
//                already-used portion (debt = limitCents - remainingCents).
//
// Goals: (revoke flow with reason gate), (RBAC perm credit:read
//        for the parent tab; revoke itself rides credit:write — guarded BE-side).
//
// Surface contract:
//   - Required textarea: reason — min 10 chars, max 500. Visible counter.
//   - Warning banner: "Đã dùng vẫn nợ — merchant sẽ phải trả $X" where the
//     debt is computed from `(limitCents - remainingCents)`. Banner is the
//     single source of truth for the user-visible side-effect of revoke.
//   - Submit disabled while reason length < 10 OR mutation pending.
//   - On success → close dialog + emit success toast "Đã thu hồi credit".
//   - On 400 CREDIT_REVOKE_REASON_REQUIRED → inline error (defensive; client
//     validation should prevent this from firing under normal flow).
//
// Sibling Task 15 hooks live at `../../api/queries/credit.queries.js` —
// imported as `useRevokeCredit` (returns `useMutation` w/ `mutate(args)`).
// If Task 15 has not landed yet, typecheck will surface the missing import.

import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useToast,
} from '@printway/ui';
import { useRevokeCredit } from '../../api/queries/credit.queries.js';
import { useTranslation } from '../../i18n/useTranslation.js';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCentsUsd(cents: number): string {
  const usd = (cents ?? 0) / 100;
  return usd.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface RevokeCreditDialogProps {
  open: boolean;
  onClose: () => void;
  creditId: string;
  merchantId: string;
  /** Limit in cents — used to compute current debt (limitCents - remainingCents). */
  limitCents: number;
  /** Remaining cents — used to compute current debt. */
  remainingCents: number;
}

const REASON_MIN = 10;
const REASON_MAX = 500;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function RevokeCreditDialog({
  open,
  onClose,
  creditId,
  merchantId,
  limitCents,
  remainingCents,
}: RevokeCreditDialogProps): JSX.Element | null {
  const { toast } = useToast();
  const { t } = useTranslation('users');
  const [reason, setReason] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);

  const revoke = useRevokeCredit();

  // Reset internal state every time the dialog re-opens — guards against
  // stale `reason` text leaking from a previous mounting.
  useEffect(() => {
    if (open) {
      setReason('');
      setServerError(null);
    }
  }, [open]);

  // Debt at this moment = the amount the merchant has already drawn down.
  // Floored at zero in case BE returns remaining > limit briefly during a
  // sweep race.
  const debtCents = useMemo(
    () => Math.max(0, (limitCents ?? 0) - (remainingCents ?? 0)),
    [limitCents, remainingCents],
  );

  const trimmedReason = reason.trim();
  const reasonValid =
    trimmedReason.length >= REASON_MIN && trimmedReason.length <= REASON_MAX;
  const submitting = revoke.isPending;
  const canSubmit = reasonValid && !submitting;

  function handleClose(): void {
    if (submitting) return;
    onClose();
  }

  async function handleSubmit(): Promise<void> {
    if (!canSubmit) return;
    setServerError(null);
    try {
      await revoke.mutateAsync({
        creditId,
        merchantId,
        reason: trimmedReason,
      });
      toast({
        title: t('revokeCredit.toast.successTitle'),
        description: t('revokeCredit.toast.successDesc'),
        variant: 'success',
      });
      onClose();
    } catch (err) {
      // Inline-render the BE message. The known 400 code from the contract
      // is `CREDIT_REVOKE_REASON_REQUIRED`; any other code surfaces its
      // human-readable message verbatim.
      const message = err instanceof Error ? err.message : t('revokeCredit.failFallback');
      const code =
        (err as { code?: string } | null | undefined)?.code ??
        'CREDIT_REVOKE_FAILED';
      if (code === 'CREDIT_REVOKE_REASON_REQUIRED') {
        setServerError(t('revokeCredit.reasonRequired'));
      } else {
        setServerError(message);
      }
    }
  }

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogHeader>
        <DialogTitle>{t('revokeCredit.title')}</DialogTitle>
      </DialogHeader>

      <div className="mt-3 space-y-4 text-sm">
        {/* Warning banner — current debt the merchant remains liable for. */}
        <div
          role="alert"
          className="rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100"
          data-testid="revoke-credit-debt-warning"
        >
          <p className="font-medium">
            {t('revokeCredit.debtWarning', { amount: formatCentsUsd(debtCents) })}
          </p>
          <p className="mt-1 text-xs">
            {t('revokeCredit.remainingNote', {
              amount: formatCentsUsd(remainingCents),
            })}
          </p>
        </div>

        <div>
          <label
            htmlFor="revoke-credit-reason"
            className="mb-1 block text-sm font-medium"
          >
            {t('revokeCredit.reasonLabel', { min: REASON_MIN, max: REASON_MAX })}
          </label>
          <textarea
            id="revoke-credit-reason"
            data-testid="revoke-credit-reason-input"
            className="block h-24 w-full rounded-md border border-pw-border bg-pw-bg p-2 text-sm focus:border-pw-primary focus:outline-none focus:ring-2 focus:ring-pw-primary/20"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={REASON_MAX}
            disabled={submitting}
          />
          <div
            className="mt-1 flex items-center justify-between text-xs text-pw-text-muted"
            data-testid="revoke-credit-reason-counter"
          >
            <span>
              {trimmedReason.length < REASON_MIN
                ? t('revokeCredit.needMoreChars', {
                    count: REASON_MIN - trimmedReason.length,
                  })
                : t('revokeCredit.enoughChars')}
            </span>
            <span>
              {reason.length} / {REASON_MAX}
            </span>
          </div>
        </div>

        {serverError ? (
          <p
            className="text-sm text-red-600 dark:text-red-400"
            data-testid="revoke-credit-error"
          >
            {serverError}
          </p>
        ) : null}
      </div>

      <DialogFooter>
        <Button variant="secondary" onClick={handleClose} disabled={submitting}>
          {t('revokeCredit.cancel')}
        </Button>
        <Button
          variant="destructive"
          onClick={handleSubmit}
          disabled={!canSubmit}
          data-testid="revoke-credit-submit"
        >
          {submitting ? t('revokeCredit.submitting') : t('revokeCredit.submit')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

export default RevokeCreditDialog;
